// src/components/wallet/ATRONRewards.js
import React, { useEffect, useState, useCallback } from 'react';
import Web3                    from 'web3';
import atronDistributorAbi     from '../../contracts/atronDistributorAbi';
import { formatCurrencyExtended } from '../../utils';
import { Button }              from 'primereact/button';
import { Skeleton }            from 'primereact/skeleton';
import { Progress }            from '@nextui-org/react';

/*
 *  ATRON reflection panel — self-contained (no parent refresh prop)
 *  ──────────────────────────────────────────────────────────────── */
const ATRONRewards = React.memo(
({ account, contracts, viewTotalDistributed = false, enableClaiming = false }) => {

  /* ────────── state ────────── */
  const [unclaimed,     setUnclaimed]     = useState(0);
  const [totalDist,     setTotalDist]     = useState(0);
  const [totalRealised, setTotalRealised] = useState(0);
  const [isClaiming,    setIsClaiming]    = useState(false);
  const [distCtr,       setDistCtr]       = useState(null);     // distributor contract
  const [isLoading,     setIsLoading]     = useState(true);

  /* ────────── 1-time contract bootstrap ────────── */
  useEffect(() => {
    const init = async () => {
      const web3 = new Web3(window.ethereum);
      const ctr  = new web3.eth.Contract(
        atronDistributorAbi,
        '0xf23467927D15454F8cF68B9C917eE6742C14C1Fd'   // hard-wired distributor
      );
      setDistCtr(ctr);
    };
    init();
  }, []);

  /* ────────── data loader ────────── */
  const loadRewards = useCallback(async () => {
    if (!distCtr || !account) return;
    setIsLoading(true);
    try {
      const [ unclaimedRaw, distRaw, shares ] = await Promise.all([
        distCtr.methods.getUnclaimedEarnings(account).call(),
        distCtr.methods.totalDistributed().call(),
        distCtr.methods.shares(account).call()
      ]);

      setUnclaimed(Number(unclaimedRaw));
      setTotalDist(Number(distRaw));
      setTotalRealised(Number(shares.totalRealised));
    } catch (e) { console.error('ATRON load error', e); }
    setIsLoading(false);
  }, [distCtr, account]);

  /* initial & 60-s polling */
  useEffect(() => { loadRewards(); }, [loadRewards]);
  useEffect(() => {
    const id = setInterval(loadRewards, 60_000);
    return () => clearInterval(id);
  }, [loadRewards]);

  /* ────────── claim handler ────────── */
  const handleClaim = async () => {
    if (!distCtr || isClaiming) return;
    setIsClaiming(true);
    try {
      await distCtr.methods.claimDividend().send({ from: account });
      await loadRewards();                       // refresh locally
    } catch (e) { console.error('ATRON claim error', e); }
    setIsClaiming(false);
  };

  /* ══════════ render ══════════ */
  return (
    <div style={{
      alignItems:'center',
      borderRadius:'10px',
      backgroundColor:'rgba(50,100,255,0.05)',
      padding:5,
      fontSize:11
    }}>

      <div>Reward: <strong>ATRON</strong></div>

      {viewTotalDistributed && (
        <div>Total Distributed:&nbsp;
          <strong>
            {isLoading
              ? <Skeleton width="100px" height="12px" />
              : formatCurrencyExtended(totalDist / 1e18)}
          </strong>
        </div>
      )}

      {(Number(totalRealised) === 0 && Number(unclaimed) === 0) &&
        <div><strong>No rewards pending</strong></div>}

      {totalRealised > 0 && (
        <div>Earned:&nbsp;
          <strong>
            {isLoading
              ? <Skeleton width="100px" height="12px" />
              : formatCurrencyExtended(totalRealised / 1e18)}
          </strong>
        </div>
      )}

      {unclaimed > 0 && (
        <div>Pending:&nbsp;
          <strong>
            {isLoading
              ? <Skeleton width="100px" height="12px" />
              : formatCurrencyExtended(unclaimed / 1e18)}
          </strong>
        </div>
      )}

      {unclaimed > 0 && enableClaiming && (
        isClaiming
          ? <Progress indeterminate style={{ marginTop:10 }} />
          : <Button
              rounded severity="warning" size="small"
              style={{ marginTop:10, width:200, justifyContent:'center' }}
              onClick={handleClaim} disabled={isClaiming}
            >
              Claim
            </Button>
      )}
    </div>
  );
});

export default ATRONRewards;
